/**
 * Bridge between React/Web Audio and WASM FM Synth
 * Provides full control over FM synthesis parameters
 */

export interface FMSynthParams {
  harmonicity?: number;          // 0.5 to 4.0
  modulationIndex?: number;      // 0 to 20
  carrierType?: number;          // 0=sine, 1=square, 2=saw, 3=triangle
  modulatorType?: number;        // 0=sine, 1=square, 2=saw, 3=triangle
  volume?: number;               // 0.0 to 1.0
  detune?: number;               // -100 to 100 cents
  portamento?: number;           // seconds
  carrierEnvelope?: {
    attack: number;
    decay: number;
    sustain: number;
    release: number;
  };
  modulationEnvelope?: {
    attack: number;
    decay: number;
    sustain: number;
    release: number;
  };
}

export class FMSynthBridge {
  private worklet: AudioWorkletNode | null = null;
  private context: AudioContext;
  private initialized: boolean = false;
  private initPromise: Promise<void> | null = null;

  constructor(context: AudioContext) {
    this.context = context;
  }

  async initialize(): Promise<AudioWorkletNode> {
    if (this.initPromise) {
      await this.initPromise;
      return this.worklet!;
    }

    this.initPromise = this._initialize();
    await this.initPromise;
    return this.worklet!;
  }

  private async _initialize(): Promise<void> {
    try {
      // Load AudioWorklet processor
      await this.context.audioWorklet.addModule('/wasm/fm-synth-processor.js');

      // Create AudioWorklet node
      this.worklet = new AudioWorkletNode(this.context, 'fm-synth-processor');

      // Fetch WASM bytes
      const wasmResponse = await fetch('/wasm/fm-synth.wasm');
      const wasmBytes = await wasmResponse.arrayBuffer();

      // Send WASM bytes to worklet (ArrayBuffer can be transferred)
      this.worklet.port.postMessage({
        type: 'init',
        wasmBytes: wasmBytes
      }, [wasmBytes]); // Transfer ownership for efficiency

      // Wait for initialization confirmation
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('WASM FM synth initialization timeout'));
        }, 5000);

        this.worklet!.port.onmessage = (event) => {
          if (event.data.type === 'initialized') {
            clearTimeout(timeout);
            if (event.data.success) {
              this.initialized = true;
              resolve();
            } else {
              reject(new Error(event.data.error || 'WASM FM init failed'));
            }
          }
        };
      });

      console.log('[WASM] FM Synth initialized successfully');
    } catch (error) {
      console.error('[WASM] FM Synth initialization failed:', error);
      throw error;
    }
  }

  noteOn(midiNote: number, velocity: number = 100): void {
    if (!this.initialized || !this.worklet) {
      console.warn('[WASM] FM Synth not initialized');
      return;
    }

    this.worklet.port.postMessage({
      type: 'noteOn',
      note: midiNote,
      velocity
    });
  }

  noteOff(): void {
    if (!this.initialized || !this.worklet) {
      return;
    }

    this.worklet.port.postMessage({
      type: 'noteOff'
    });
  }

  // FM Synthesis Parameters

  setHarmonicity(value: number): void {
    if (!this.initialized || !this.worklet) return;

    this.worklet.port.postMessage({
      type: 'setHarmonicity',
      value: Math.max(0.5, Math.min(4.0, value))
    });
  }

  setModulationIndex(value: number): void {
    if (!this.initialized || !this.worklet) return;

    this.worklet.port.postMessage({
      type: 'setModulationIndex',
      value: Math.max(0, Math.min(20, value))
    });
  }

  setCarrierType(oscType: number): void {
    if (!this.initialized || !this.worklet) return;

    this.worklet.port.postMessage({
      type: 'setCarrierType',
      value: Math.floor(Math.max(0, Math.min(3, oscType)))
    });
  }

  setModulatorType(oscType: number): void {
    if (!this.initialized || !this.worklet) return;

    this.worklet.port.postMessage({
      type: 'setModulatorType',
      value: Math.floor(Math.max(0, Math.min(3, oscType)))
    });
  }

  // Envelopes

  setCarrierEnvelope(attack: number, decay: number, sustain: number, release: number): void {
    if (!this.initialized || !this.worklet) return;

    this.worklet.port.postMessage({
      type: 'setCarrierEnvelope',
      attack: Math.max(0.001, attack),
      decay: Math.max(0.001, decay),
      sustain: Math.max(0, Math.min(1, sustain)),
      release: Math.max(0.001, release)
    });
  }

  setModulationEnvelope(attack: number, decay: number, sustain: number, release: number): void {
    if (!this.initialized || !this.worklet) return;

    this.worklet.port.postMessage({
      type: 'setModulationEnvelope',
      attack: Math.max(0.001, attack),
      decay: Math.max(0.001, decay),
      sustain: Math.max(0, Math.min(1, sustain)),
      release: Math.max(0.001, release)
    });
  }

  // Other Parameters

  setVolume(value: number): void {
    if (!this.initialized || !this.worklet) return;

    this.worklet.port.postMessage({
      type: 'setVolume',
      value: Math.max(0, Math.min(1, value))
    });
  }

  setDetune(cents: number): void {
    if (!this.initialized || !this.worklet) return;

    this.worklet.port.postMessage({
      type: 'setDetune',
      value: Math.max(-100, Math.min(100, cents))
    });
  }

  setPortamento(seconds: number): void {
    if (!this.initialized || !this.worklet) return;

    this.worklet.port.postMessage({
      type: 'setPortamento',
      value: Math.max(0, seconds)
    });
  }

  // Bulk parameter update
  updateParams(params: FMSynthParams): void {
    if (params.harmonicity !== undefined) {
      this.setHarmonicity(params.harmonicity);
    }
    if (params.modulationIndex !== undefined) {
      this.setModulationIndex(params.modulationIndex);
    }
    if (params.carrierType !== undefined) {
      this.setCarrierType(params.carrierType);
    }
    if (params.modulatorType !== undefined) {
      this.setModulatorType(params.modulatorType);
    }
    if (params.volume !== undefined) {
      this.setVolume(params.volume);
    }
    if (params.detune !== undefined) {
      this.setDetune(params.detune);
    }
    if (params.portamento !== undefined) {
      this.setPortamento(params.portamento);
    }
    if (params.carrierEnvelope) {
      this.setCarrierEnvelope(
        params.carrierEnvelope.attack,
        params.carrierEnvelope.decay,
        params.carrierEnvelope.sustain,
        params.carrierEnvelope.release
      );
    }
    if (params.modulationEnvelope) {
      this.setModulationEnvelope(
        params.modulationEnvelope.attack,
        params.modulationEnvelope.decay,
        params.modulationEnvelope.sustain,
        params.modulationEnvelope.release
      );
    }
  }

  connect(destination: AudioNode): void {
    if (this.worklet) {
      this.worklet.connect(destination);
    }
  }

  disconnect(): void {
    if (this.worklet) {
      this.worklet.disconnect();
    }
  }

  dispose(): void {
    this.disconnect();
    this.worklet = null;
    this.initialized = false;
    this.initPromise = null;
  }

  getNode(): AudioWorkletNode | null {
    return this.worklet;
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}
